"use client"

import type React from "react"

import { useState } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../shared/functions/Context"


interface ShareScorecardModalProps {
  onClose: () => void
  onShare: (emails: string[], message: string) => Promise<void>
  title: string
  isLoading?: boolean
}

const ShareScorecardModal = observer((props: ShareScorecardModalProps) => {
  const { onClose, onShare, title, isLoading = false } = props
  const { ui } = useAppContext()

  const [emails, setEmails] = useState<string>("")
  const [message, setMessage] = useState("")
  const [emailError, setEmailError] = useState("")
  const [loading, setLoading] = useState(false)

  // Validate email addresses
  const validateEmails = (emailString: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const emailList = emailString.split(",").map((email) => email.trim())

    for (const email of emailList) {
      if (email && !emailRegex.test(email)) {
        setEmailError(`Invalid email format: ${email}`)
        return false
      }
    }

    if (emailList.length === 0 || (emailList.length === 1 && !emailList[0])) {
      setEmailError("Please enter at least one email address")
      return false
    }

    setEmailError("")
    return true
  }

  const handleShare = async () => {
    if (!validateEmails(emails)) return

    setLoading(true)
    try {
      const emailList = emails
        .split(",")
        .map((email) => email.trim())
        .filter(Boolean)
      await onShare(emailList, message)

      ui.snackbar.load({
        id: Date.now(),
        message: "Scorecard shared successfully!",
        type: "success",
      })

      onClose()
    } catch (error) {
      console.error("Error sharing scorecard:", error)
      ui.snackbar.load({
        id: Date.now(),
        message: "Failed to share scorecard. Please try again.",
        type: "danger",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmails(e.target.value)
    if (emailError) {
      setEmailError("")
    }
  }

  return (
    <div className="uk-modal-dialog uk-modal-body uk-margin-auto-vertical">
      <button
        className="uk-modal-close-default"
        type="button"
        data-uk-close
        onClick={onClose}
        disabled={loading || isLoading}
      ></button>

      <div className="dialog">
        <div className="dialog-header">
          <h3 className="dialog-heading">Share Scorecard via Email</h3>
          <p className="uk-text-muted">Share "{title}" scorecard as PDF attachment</p>
        </div>

        <div className="dialog-content uk-position-relative">
          <form className="uk-form-stacked">
            {/* Email Recipients */}
            <div className="uk-margin">
              <label className="uk-form-label" htmlFor="email-recipients">
                Email Recipients *
              </label>
              <div className="uk-form-controls">
                <input
                  id="email-recipients"
                  className={`uk-input ${emailError ? "uk-form-danger" : ""}`}
                  type="text"
                  placeholder="Enter email addresses separated by commas"
                  value={emails}
                  onChange={handleEmailChange}
                  disabled={loading || isLoading}
                />
                <div className="uk-text-small uk-text-muted uk-margin-small-top">
                  Separate multiple email addresses with commas
                </div>
                {emailError && <div className="uk-text-danger uk-text-small uk-margin-small-top">{emailError}</div>}
              </div>
            </div>

            {/* Message */}
            <div className="uk-margin">
              <label className="uk-form-label" htmlFor="email-message">
                Message (Optional)
              </label>
              <div className="uk-form-controls">
                <textarea
                  id="email-message"
                  className="uk-textarea"
                  rows={4}
                  placeholder="Add a personal message to include in the email..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={loading || isLoading}
                />
              </div>
            </div>

            {/* Preview */}
            <div className="uk-margin">
              <div className="uk-card uk-card-default uk-card-body uk-card-small">
                <h6 className="uk-card-title uk-margin-remove-bottom">Email Preview</h6>
                <div className="uk-text-small uk-text-muted">
                  <p>
                    <strong>Subject:</strong> Scorecard: {title}
                  </p>
                  <p>
                    <strong>Attachment:</strong> {title}-Scorecard.pdf
                  </p>
                  {message && (
                    <p>
                      <strong>Message:</strong> {message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>

        <div className="dialog-footer">
          <div className="uk-flex uk-flex-right">
            <button
              className="uk-button uk-button-default uk-margin-right"
              type="button"
              onClick={onClose}
              disabled={loading || isLoading}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary uk-margin-small-right"
              type="button"
              onClick={handleShare}
              disabled={loading || isLoading || !emails.trim()}
            >
              {loading || isLoading ? (
                <>
                  <div data-uk-spinner="ratio: 0.5" className="uk-margin-small-right"></div>
                  Sharing...
                </>
              ) : (
                <>
                  <span data-uk-icon="mail" className="uk-margin-small-right"></span>
                  Share Scorecard
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
})

export default ShareScorecardModal
